import { FC, useCallback, useMemo, useEffect } from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"

// components
import { TabContent } from "./TabContent"
import { TabItem } from "./TabItem"
import { PersonalizationSection } from "../../PersonalizationSection/PersonalizationSection"
import { LogIn } from "../../LogIn/LogIn"

// redux
import { RootState } from "../../../store/store"
import { setAudience } from "../../../store/slices/audience.slice"

// hooks
import { usePersonalizationComponent } from "../../../hooks/usePersonalizationComponent"

// styles
import * as TabsStyles from "./Tabs.module.scss"
import { homepageTabUl } from "../Tabs/TabItem.module.scss"
import * as globalStyles from "../../../theme/global.module.scss"

// types
import { BaseState, setMetaData } from "../../../store/slices/metadata.slice"
import { userProfileGuardCheck } from "../../../store/slices/user_profile.slice"

type HomepageLandingPage = NonNullable<
  Queries.HomepageDataQuery["helpCenterHomePage"]
>
type AttendeeLandingPage = HomepageLandingPage["attendee"]
type OrganizerLandingPage = HomepageLandingPage["organizer"]
type DefaultLandingPage = HomepageLandingPage["default"]

type TicketProps = {
  noTicketsFoundActionComplex: Queries.HomepageDataQuery["noTicketsFoundActionComplex"]
  ticketsFoundActionComplex: Queries.HomepageDataQuery["ticketsFoundActionComplex"]
  messageNoUpcomingOrders: Queries.HomepageDataQuery["messageNoUpcomingOrders"]
  searchingForTickets: Queries.HomepageDataQuery["searchingForTickets"]
}

type EventProps = {
  noEventsFoundActionComplex: Queries.HomepageDataQuery["noEventsFoundActionComplex"]
  eventsFoundActionComplex: Queries.HomepageDataQuery["eventsFoundActionComplex"]
  searchingForEvents: Queries.HomepageDataQuery["searchingForEvents"]
  messageNoUpcomingEvents: Queries.HomepageDataQuery["messageNoUpcomingEvents"]
}

type LoginProps = {
  loginHeaderMicrocopy: Queries.HelpArticleQuery["loginHeaderMicrocopy"]
  loginContentMicrocopy: Queries.HelpArticleQuery["loginContentMicrocopy"]
  loginButtonMicrocopy: Queries.HelpArticleQuery["loginButtonMicrocopy"]
  cantLogInMicrocopy?: Queries.HelpArticleQuery["cantLogInMicrocopy"]
  cantLogInArticle?: Queries.HelpArticleQuery["cantLogInArticle"]
}

export type Props = {
  attendee: AttendeeLandingPage | null | undefined
  dflt: DefaultLandingPage | null | undefined
  organizer: OrganizerLandingPage | null | undefined
  prefixedBasePath: string
} & TicketProps &
  EventProps &
  LoginProps

export const getTabId = (keyValue: string | number) => `tab${keyValue}`
export const getPanelId = (keyValue: string | number) => `panel${keyValue}`

export const Tabs: FC<Props> = (props) => {
  const {
    attendee,
    dflt,
    organizer,
    prefixedBasePath,
    noTicketsFoundActionComplex,
    ticketsFoundActionComplex,
    messageNoUpcomingOrders,
    searchingForTickets,
    noEventsFoundActionComplex,
    eventsFoundActionComplex,
    searchingForEvents,
    messageNoUpcomingEvents,
    loginContentMicrocopy,
    loginHeaderMicrocopy,
    loginButtonMicrocopy,
    cantLogInMicrocopy,
    cantLogInArticle
  } = props

  const audience = useSelector((state: RootState) => state.audience) as
    | string
    | null

  const userProfile = userProfileGuardCheck(
    useSelector((state: RootState) => state.userProfile)
  )
  const isOrganizer = userProfile?.isOrganizer
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )

  const dispatch = useDispatch()

  // Use custom hook fo personalization logic
  const { activePersonalizationComponent } = usePersonalizationComponent()

  /****
   * If audience is null when a user lands on the homepage,
   * set the value to the default based on user profile
   * Wait for authentication to complete for more reliable user data
   * ****/
  useEffect(() => {
    if (audience === null && hasAttemptedLogin) {
      if (isOrganizer) {
        if (organizer?.title) {
          dispatch(setAudience(organizer.title))
        }
        dispatch(
          setMetaData({
            audienceEnglishCategory:
              organizer?.landingPageVariationInternalTitle as
                | "Homepage variation: Attendee"
                | "Homepage variation: Organizer"
          })
        )
      } else {
        if (attendee?.title) {
          dispatch(setAudience(attendee.title))
        }
        dispatch(
          setMetaData({
            audienceEnglishCategory:
              attendee?.landingPageVariationInternalTitle as
                | "Homepage variation: Attendee"
                | "Homepage variation: Organizer"
          })
        )
      }
    }
  }, [audience, isOrganizer, attendee, organizer, dispatch, hasAttemptedLogin])

  const handleSelect = useCallback(
    (
      tabKey: string,
      audienceEnglishCategory: BaseState["audienceEnglishCategory"]
    ) => {
      dispatch(setAudience(tabKey))
      dispatch(
        setMetaData({
          audienceEnglishCategory
        })
      )
    },
    [dflt, attendee, organizer]
  )

  const tabItems = useMemo(() => {
    const items = []

    items.push(
      <TabItem
        key={attendee?.title}
        onSelected={handleSelect}
        displayName={attendee?.title ?? ""}
        isSelected={attendee?.title === audience}
        landingPageVariationInternalTitle={
          attendee?.landingPageVariationInternalTitle as
            | "Homepage variation: Attendee"
            | "Homepage variation: Organizer"
        }
      />
    )
    items.push(
      <TabItem
        key={organizer?.title}
        onSelected={handleSelect}
        displayName={organizer?.title ?? ""}
        isSelected={organizer?.title === audience}
        landingPageVariationInternalTitle={
          organizer?.landingPageVariationInternalTitle as
            | "Homepage variation: Attendee"
            | "Homepage variation: Organizer"
        }
        tourStepAttr="organizer-tab"
      />
    )
    return items
  }, [attendee, organizer, audience, handleSelect])

  return (
    <>
      <LogIn
        loginButtonMicrocopy={loginButtonMicrocopy}
        loginContentMicrocopy={loginContentMicrocopy}
        loginHeaderMicrocopy={loginHeaderMicrocopy}
        cantLogInMicrocopy={cantLogInMicrocopy}
        cantLogInArticle={cantLogInArticle}
      />
      <nav
        className={classNames(
          // @ts-expect-error typed-scss-modules generates `edsLPadVert_3`; webpack css-loader exports `edsLPadVert3`. See README "Stylesheets".
          globalStyles.edsLPadVert3,
          // @ts-expect-error typed-scss-modules generates `edsLMarBot_3`; webpack css-loader exports `edsLMarBot3`. See README "Stylesheets".
          globalStyles.edsLMarBot3
        )}
      >
        <ul className={homepageTabUl}>{tabItems}</ul>
      </nav>
      <PersonalizationSection
        noTicketsFoundActionComplex={noTicketsFoundActionComplex}
        ticketsFoundActionComplex={ticketsFoundActionComplex}
        messageNoUpcomingOrders={messageNoUpcomingOrders}
        searchingForTickets={searchingForTickets}
        noEventsFoundActionComplex={noEventsFoundActionComplex}
        eventsFoundActionComplex={eventsFoundActionComplex}
        searchingForEvents={searchingForEvents}
        messageNoUpcomingEvents={messageNoUpcomingEvents}
        loginButtonMicrocopy={loginButtonMicrocopy}
        loginContentMicrocopy={loginContentMicrocopy}
        loginHeaderMicrocopy={loginHeaderMicrocopy}
        containerClassName={
          activePersonalizationComponent === "attendee"
            ? TabsStyles?.personalizationAttendeeContainer
            : activePersonalizationComponent === "organizer"
              ? TabsStyles?.personalizationOrganizerContainer
              : undefined
        }
      />
      <TabContent
        prefixedBasePath={prefixedBasePath}
        segment={
          (audience === organizer?.title ? organizer : attendee) ?? null
        }
      />
    </>
  )
}
